/**
 * ADR-129 §6 — client-side localized-frame swap contract.
 *
 * App-agnostic core of the swap-on-localized contract, shared by AIM web
 * and SMS web. The app-specific swap effect — AIM's redux
 * title dispatch, SMS's React Query cache patch — stays app-local; this lib
 * owns detection, staleness keying, the accept-window tracker, and the
 * translated-field extraction.
 *
 * The server delivers every alert in the operational language
 * (English) FIRST on the normal path, then asynchronously re-broadcasts a
 * localized copy per audience language on the SAME `object.data` /
 * type:"Alert" channel. A localized re-broadcast frame carries envelope keys
 * the operational frame lacks:
 *   - language            (e.g. "es")
 *   - translation_version (per-worker monotonic token — NOT globally orderable)
 *   - alert_id            (Alert frames only; the id of the alert being localized)
 * Operational and localized frames for the same broadcast share the same
 * `timestamp` (the server reuses `update_time`), which is the robust staleness
 * key. See `send_localized_websocket_update_v2` in
 * `apps/aegix-aim-backend/server/api/v2/sockets.py`.
 *
 * Client contract:
 *   1. Render the operational frame immediately (the normal handler path).
 *   2. When the localized frame arrives, swap the translated field(s) into the
 *      already-rendered alert, matched by alert_id — never replay the alert.
 *   3. Failover: if no localized frame arrives within T_ws, keep operational text.
 *   4. Stale-drop: ignore a localized frame older than the latest operational frame
 *      rendered for that alert_id (keyed on the frame timestamp, since
 *      translation_version is only per-worker monotonic).
 *
 * For Alert frames the single server-translated field is the nested alert-type
 * display name: `data.type.name` (see tiaki `translate_websocket_payload` +
 * `api/v2/tests/test_alert_translation.py`).
 *
 * Invariant (server-guaranteed): the envelope `alert_id` equals the alert
 * payload's `data.id` — the server sets `alert_id = body.get("id")` from the
 * same serialized alert (`api/v2/sockets.py`). So the operational frame (keyed
 * by `data.id`) and the localized frame (matched by `alert_id`) address the
 * same tracker window.
 *
 * @see doc/adr/decisions/129-real-time-translation-delivery.md §6
 */

/** Default localized-frame accept window (T_ws). Operational text is already shown,
 *  so this only bounds how long a following localized frame is accepted. */
export const DEFAULT_T_WS_MS = 5000;

export interface SocketEnvelope {
  type?: string;
  timestamp?: string | number;
  language?: string;
  translation_version?: string;
  alert_id?: number;
  [key: string]: unknown;
}

/**
 * A re-broadcast frame is any frame carrying both `language` and
 * `translation_version`. These are stamped by the server only on the async
 * localized re-broadcast, never on the operational delivery.
 */
export function isLocalizedRebroadcast(raw: unknown): raw is SocketEnvelope {
  if (!raw || typeof raw !== 'object') return false;
  const r = raw as SocketEnvelope;
  return (
    typeof r.language === 'string' &&
    r.language.length > 0 &&
    r.translation_version != null &&
    String(r.translation_version).length > 0
  );
}

/**
 * Numeric epoch for staleness comparison. Tolerates the server's numeric
 * `update_time` as well as ISO-string timestamps; returns 0 when absent.
 */
export function parseFrameTimestamp(
  raw: SocketEnvelope | null | undefined,
): number {
  const ts = raw?.timestamp;
  if (typeof ts === 'number') return ts;
  if (typeof ts === 'string') {
    const numeric = Number(ts);
    if (!Number.isNaN(numeric)) return numeric;
    const parsed = Date.parse(ts);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return 0;
}

/**
 * The translated alert-type display name from a localized Alert frame's inner
 * data (`data.type.name`), or null if absent/empty. This is the only field the
 * server translates for Alert frames.
 */
export function extractLocalizedAlertTypeName(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const type = (data as { type?: unknown }).type;
  if (
    type &&
    typeof type === 'object' &&
    typeof (type as { name?: unknown }).name === 'string'
  ) {
    const name = (type as { name: string }).name;
    return name.length > 0 ? name : null;
  }
  return null;
}

/**
 * Tracks the operational-frame accept window per alert_id so localized frames can
 * be accepted (within T_ws and not stale) or dropped. Encapsulates the failover
 * timers; framework-free so it is unit-testable in isolation.
 */
export class AlertLocalizationTracker {
  private readonly lastOperationalTs = new Map<number, number>();
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  constructor(private readonly windowMs: number = DEFAULT_T_WS_MS) {}

  /**
   * Record an operational Alert frame render; (re)opens the T_ws accept window
   * for this alert_id. A later operational frame supersedes the previous one.
   */
  recordOperational(alertId: number, timestamp: number): void {
    if (!Number.isFinite(alertId)) return;
    this.lastOperationalTs.set(alertId, timestamp);
    this.restartTimer(alertId);
  }

  /**
   * Whether a localized frame for `alertId` should be applied: an operational
   * frame is still within its T_ws window (failover) AND the localized frame is
   * not older than the latest operational frame for that alert (stale-drop).
   */
  shouldApply(alertId: number, localizedTimestamp: number): boolean {
    const lastOp = this.lastOperationalTs.get(alertId);
    if (lastOp === undefined) return false; // no open window: never rendered, or T_ws expired
    return localizedTimestamp >= lastOp; // stale-drop superseded broadcasts
  }

  /** Close the accept window after a localized frame has been applied. */
  markApplied(alertId: number): void {
    this.clearTimer(alertId);
    this.lastOperationalTs.delete(alertId);
  }

  /** Cancel all timers and clear state (call on component unmount). */
  dispose(): void {
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
    this.lastOperationalTs.clear();
  }

  private restartTimer(alertId: number): void {
    this.clearTimer(alertId);
    const timer = setTimeout(() => {
      this.lastOperationalTs.delete(alertId);
      this.timers.delete(alertId);
    }, this.windowMs);
    this.timers.set(alertId, timer);
  }

  private clearTimer(alertId: number): void {
    const timer = this.timers.get(alertId);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(alertId);
    }
  }
}
